import { Fuel, Drill, Radar } from "lucide-react";
import { GEM_SPRITE, GEM_BONUS } from "./navShared";
import { worldMeta, navGoals, FUEL_CELL } from "./navShared";

// Moon fuel gauge shown above the grid: green -> amber -> red as it drops, pulses when a fuel cell is picked up
export const NavFuelBar = ({ nav, fuel, flash }) => {
  const start = nav?.fuel ?? 0;
  const max = Math.max(start + FUEL_CELL * (nav?.fuel_cells || []).length, 1);
  const value = fuel ?? start;
  const ratio = Math.max(0, Math.min(1, value / max));
  const level = value <= 0 ? "empty" : ratio > 0.5 ? "high" : ratio > 0.25 ? "mid" : "low";
  const colour = { high: "bg-emerald-500", mid: "bg-amber-500", low: "bg-red-500", empty: "bg-red-700" }[level];
  return (
    <div className={`flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 ${flash === "fuel" ? "animate-pulse ring-2 ring-emerald-400/70" : ""}`} data-testid="nav-fuel-bar" data-fuel={value} data-level={level}>
      <Fuel className={`w-4 h-4 ${level === "high" ? "text-emerald-600" : level === "mid" ? "text-amber-600" : "text-red-600"}`} />
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fuel</span>
      <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden min-w-[80px]">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${ratio * 100}%`, transition: "width 250ms ease-out, background-color 250ms" }} />
      </div>
      <span className="text-sm font-bold tabular-nums w-7 text-right" data-testid="nav-fuel-value">{value}</span>
    </div>
  );
};

// "1 of 2 diamonds" pill for worlds with several collectables
export const NavGoalCounter = ({ nav, collected }) => {
  const total = navGoals(nav).length;
  if (total < 2) return null;
  const world = worldMeta(nav);
  const n = navGoals(nav).filter((g) => collected.has(`${g.x},${g.y}`)).length;
  return (
    <div className="absolute top-2 right-2 flex items-center gap-1.5 rounded-full bg-black/60 text-white px-2.5 py-1 text-xs font-semibold backdrop-blur-sm" data-testid="nav-goal-counter" data-collected={n} data-total={total}>
      <img src={world.sprites.goal} alt="" className="w-4 h-4" draggable={false} />
      {n} of {total} {world.labels.goals}
    </div>
  );
};

// "Drill ON ×1" / "Drill off" pill, only on grids that have activate-drill power-ups
export const NavDrillStatus = ({ nav, charges }) => {
  if (!(nav?.powers || []).length) return null;
  const on = charges > 0;
  return (
    <div className={`absolute top-2 left-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold backdrop-blur-sm ${on ? "bg-orange-500 text-white shadow-[0_0_12px_rgba(249,115,22,0.8)]" : "bg-black/60 text-white/80"}`} data-testid="nav-drill-status" data-charges={charges}>
      <Drill className={`w-3.5 h-3.5 ${on ? "animate-pulse" : ""}`} />
      {on ? `Drill ON${charges > 1 ? ` ×${charges}` : ""}` : "Drill off"}
    </div>
  );
};

// Bottom-left: "Scanner on" while a loop / if is running; "Hidden gems 2 · +20" once the scanner has revealed some
export const NavGemBadge = ({ count = 0, scanning }) => {
  if (count > 0) {
    return (
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-2.5 py-1 text-xs font-semibold shadow-[0_0_12px_rgba(16,185,129,0.8)]" data-testid="nav-gem-badge" data-count={count}>
        <img src={GEM_SPRITE} alt="" className="w-3.5 h-3.5" draggable={false} /> {count === 1 ? "Hidden gem" : `${count} hidden gems`} +{count * GEM_BONUS}
      </div>
    );
  }
  if (!scanning) return null;
  return (
    <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-emerald-950/70 text-emerald-200 px-2.5 py-1 text-xs font-semibold backdrop-blur-sm" data-testid="nav-scanner-badge">
      <Radar className="w-3.5 h-3.5 animate-pulse" /> Scanner on
    </div>
  );
};
